"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
// Possibly apply a function to a possibly undefined value
function flatMapM(f, a) {
    if (typeof a === 'undefined' || a === null) {
        return undefined;
    }
    return f(a);
}
exports.flatMapM = flatMapM;
// Take the default if a value is undefined
function def(d, a) {
    return typeof a === 'undefined' || a === null ? d : a;
}
exports.def = def;
// Take the value produced by a function if a value is undefined
function defF(f, a) {
    return typeof a === 'undefined' || a === null ? f() : a;
}
exports.defF = defF;
// Identity. Return thyself.
function id(a) {
    return a;
}
exports.id = id;
// Build a function the disregards it paramaters and always
// returns the same result.
function constant(a) {
    return function (_) { return a; };
}
exports.constant = constant;
// Return a function that has the order of its input parameters reversed.
function flip(fn) {
    return function (b, a) { return fn(a, b); };
}
exports.flip = flip;
// Compose two functions together
function dot(g, f) {
    return function (a) { return g(f(a)); };
}
exports.dot = dot;
// Build a function that returns an a.
function thunk(a) {
    return function () { return a; };
}
exports.thunk = thunk;
// Compute the return value only the first time the function is called.
// Subsiquent invocations reuse that value.
function memoize(f) {
    var res;
    return function () {
        return (res = defF(f, res));
    };
}
exports.memoize = memoize;
function notImplemented() {
    throw new Error('Not implemented');
}
exports.notImplemented = notImplemented;
function Void() {
    return undefined;
}
exports.Void = Void;
// An implementation of the reader monad. An abstraction
// for a function that will eventually provide
// a U, given a T.
var Reader = /** @class */ (function () {
    function Reader(f) {
        this.f = f;
    }
    Reader.ask = function (f) {
        return new Reader(f);
    };
    // This probably shouldn't be here. But it provides clearer syntax
    // at the call site. Unlike `ask`, which is very specific to Reader,
    // this `all` thing could apply to almost anything.
    Reader.all = function (f, us) {
        return new Reader(function (t) { return us.map(function (u) { return f(u).run(t); }); });
    };
    Reader.pure = function (v) {
        return new Reader(function (_) { return v; });
    };
    Reader.prototype.map = function (g) {
        return new Reader(dot(g, this.f));
    };
    // Notice that our flatMap allows for broadening of T to T & T2.
    // Might as well make use of structural typing.
    // Notice that `f<T extends SomeEnv>(): Reader<T, V>` doesn't work
    // like we want, so this is the best we can do.
    Reader.prototype.flatMap = function (g) {
        var _this = this;
        return new Reader(function (t) { return g(_this.f(t)).run(t); });
    };
    Reader.prototype.run = function (t) {
        return this.f(t);
    };
    // If T can be derived from W, we narrow the requirement
    // on the reader to W by by injecting a narrowing function
    Reader.prototype.narrow = function (r) {
        var _this = this;
        return new Reader(function (w) { return _this.f(r.run(w)); });
    };
    // Like map, but g is called before f
    Reader.prototype.lmap = function (g) {
        var _this = this;
        return new Reader(function (w) { return _this.f(g(w)); });
    };
    return Reader;
}());
exports.Reader = Reader;
exports.ask = function (f) { return new Reader(f); };
// A haphazard monad transformer for using Reader<T, V> with V = Promise<U>.
// because we don't have HKT.
var ReaderP = /** @class */ (function () {
    function ReaderP(r) {
        this.r = r;
    }
    ReaderP.ask = function (f) {
        return new ReaderP(Reader.ask(f));
    };
    ReaderP.liftReader = function (r) {
        return ReaderP.ask(function (t) { return Promise.resolve(r.run(t)); });
    };
    ReaderP.sequence = function (rs) {
        return ReaderP.ask(function (t) { return Promise.all(rs.map(function (r) { return r.run(t); })); });
    };
    ReaderP.pure = function (v) {
        return new ReaderP(Reader.pure(Promise.resolve(v)));
    };
    ReaderP.fromPromise = function (promise) {
        return ReaderP.ask(function () { return promise(); });
    };
    // As of typescript 3.5 The fact that ReaderP can't express contravariance
    // in T is becoming an issue. Given
    // interface E1 {...}
    // interface E2 extends E1 {...}
    // function example(r: ReaderP<E1, void>): ReaderP<E2, void> {
    //    // not assignable event tho E2 is assignable to E1 and hence
    //    // meets the needs of the computation.
    //    return r;
    // }
    // The solution is to explicitly unpack e so that the assignment occurs
    // at teh E1 := E2 level instead of the ReaderP<E1...> := ReaderP<E2...>
    // level. TODO: give this a better name BM (24/07/19)
    ReaderP.shim = function (r) {
        return ReaderP.ask(function (e) { return r.run(e); });
    };
    ReaderP.prototype.map = function (f) {
        return new ReaderP(this.r.map(function (p) { return p.then(f); }));
    };
    // Broadening of T to T & T2 again. Still seems like a good idea.
    ReaderP.prototype.flatMap = function (f) {
        var _this = this;
        return ReaderP.ask(function (t) { return _this.r.run(t).then(function (v) { return f(v).run(t); }); });
    };
    // Note that this is more restrictive that `Promise.catch`
    // which has the type Promose.catch(c: (e: any) => any).
    ReaderP.prototype.catch = function (f) {
        var _this = this;
        return ReaderP.ask(function (t) { return _this.r.run(t).catch(f); });
    };
    ReaderP.prototype.run = function (t) {
        return this.r.run(t);
    };
    // If T can be derived from W, we narrow the requirements of the
    // reader to W by using r.
    ReaderP.prototype.narrow = function (r) {
        var _this = this;
        return ReaderP.ask(function (w) { return r.run(w).then(function (t) { return _this.r.run(t); }); });
    };
    // Notice that this is a profunctor, so we're mapping
    // back through the input
    ReaderP.prototype.lmap = function (g) {
        return new ReaderP(this.r.lmap(g));
    };
    ReaderP.sequence2 = function (_a) {
        var rv1 = _a[0], rv2 = _a[1];
        return ReaderP.ask(function (t) { return Promise.all([rv1.run(t), rv2.run(t)]); });
    };
    ReaderP.sequence3 = function (_a) {
        var rv1 = _a[0], rv2 = _a[1], rv3 = _a[2];
        return ReaderP.ask(function (t) { return Promise.all([rv1.run(t), rv2.run(t), rv3.run(t)]); });
    };
    return ReaderP;
}());
exports.ReaderP = ReaderP;
var Just = /** @class */ (function () {
    function Just(t) {
        this.t = t;
    }
    Just.prototype.map = function (f) {
        return new Just(f(this.t));
    };
    Just.prototype.flatMap = function (f) {
        return f(this.t);
    };
    Just.prototype.getOrElse = function (t) {
        return this.t;
    };
    Just.prototype.lGetOrElse = function (t) {
        return this.t;
    };
    Just.prototype.get = function () {
        return this.t;
    };
    Just.prototype.filter = function (f) {
        if (f(this.t)) {
            return this;
        }
        return new None();
    };
    Just.prototype.isJust = function () {
        return true;
    };
    Just.prototype.isNone = function () {
        return false;
    };
    return Just;
}());
exports.Just = Just;
var None = /** @class */ (function () {
    function None() {
    }
    None.prototype.map = function (f) {
        return new None();
    };
    None.prototype.flatMap = function (f) {
        return new None();
    };
    None.prototype.getOrElse = function (t) {
        return t;
    };
    None.prototype.lGetOrElse = function (t) {
        return t();
    };
    None.prototype.get = function () {
        throw new Error('Optional.get was called on None');
    };
    None.prototype.filter = function (f) {
        return this;
    };
    None.prototype.isJust = function () {
        return false;
    };
    None.prototype.isNone = function () {
        return true;
    };
    return None;
}());
exports.None = None;
function none() {
    return new None();
}
exports.none = none;
function just(t) {
    return new Just(t);
}
exports.just = just;
function optional(t) {
    if (typeof t === 'undefined') {
        return new None();
    }
    return new Just(t);
}
exports.optional = optional;
function try_(f) {
    try {
        return just(f());
    }
    catch (e) {
        return none();
    }
}
exports.try_ = try_;
function filterJust(options) {
    return options.reduce(function (carry, value) {
        if (value.isJust()) {
            carry.push(value.get());
        }
        return carry;
    }, []);
}
exports.filterJust = filterJust;
function filterDefined(partials) {
    return partials.reduce(function (carry, value) {
        if (value !== undefined) {
            carry.push(value);
        }
        return carry;
    }, []);
}
exports.filterDefined = filterDefined;
function get(arr, idx) {
    if (idx >= 0 && idx < arr.length) {
        return new Just(arr[idx]);
    }
    return new None();
}
exports.get = get;
function head(arr) {
    return get(arr, 0);
}
exports.head = head;
var Pair = /** @class */ (function () {
    function Pair(l, r) {
        this.l = l;
        this.r = r;
    }
    Pair.prototype.left = function () {
        return this.l;
    };
    Pair.prototype.right = function () {
        return this.r;
    };
    Pair.prototype.bimap = function (fl, fr) {
        return new Pair(fl(this.l), fr(this.r));
    };
    Pair.prototype.map = function (fr) {
        return new Pair(this.l, fr(this.r));
    };
    return Pair;
}());
exports.Pair = Pair;
function pair(l, r) {
    return new Pair(l, r);
}
exports.pair = pair;
